<script setup>
import { ref, watch, computed, onUnmounted } from 'vue';
import { GoogleMap } from 'vue3-google-map';
import { storeToRefs } from 'pinia';
import { usePageBrandStore } from '@stores/usePageBrandStore';
import { DefaultViewport, ViewType } from '@components-molecules/map/map-constants';
import StickyTabs from '@components-molecules/map/StickyTabs.vue';
import PageSpaceMarker from '@organisms/page-brand/map/components/PageSpaceMarker.vue';

/* global google */
const store = usePageBrandStore();

const { paginatedPageSpaces, primaryPageSpaceMarkers, secondaryPageSpaceMarkers, mobileView } = storeToRefs(store);
const { setMobileView } = store;

// @TODO Centralize in pinia store, see https://bureaux-a-partager.atlassian.net/browse/MATCH-9839
const isMobileOrTablet = ref(window.BREAKPOINTS.isMobile || window.BREAKPOINTS.isTablet);
const isMapDisplayed = computed(() => !isMobileOrTablet.value || mobileView.value === ViewType.MAP);

const apiKey = window.GOOGLE_API_KEY;
const mapRef = ref(null);

const handleResize = () => {
  isMobileOrTablet.value = window.BREAKPOINTS.isMobile || window.BREAKPOINTS.isTablet;
};

window.addEventListener('resize', handleResize);

const getBounds = () => {
  if (!paginatedPageSpaces.value.length) {
    // France coordinates
    return new google.maps.LatLngBounds(
      new google.maps.LatLng(DefaultViewport.SOUTH, DefaultViewport.WEST), // South West
      new google.maps.LatLng(DefaultViewport.NORTH, DefaultViewport.EAST), // North East
    );
  }

  const { minLat, maxLat, minLong, maxLong } = paginatedPageSpaces.value.reduce(
    (bounds, { latitude, longitude }) => ({
      minLat: Math.min(bounds.minLat, latitude),
      maxLat: Math.max(bounds.maxLat, latitude),
      minLong: Math.min(bounds.minLong, longitude),
      maxLong: Math.max(bounds.maxLong, longitude),
    }),
    {
      minLat: Infinity,
      maxLat: -Infinity,
      minLong: Infinity,
      maxLong: -Infinity,
    },
  );

  return new google.maps.LatLngBounds(
    new google.maps.LatLng(minLat, minLong),
    new google.maps.LatLng(maxLat, maxLong),
  );
};

watch(() => mapRef.value?.ready, (ready) => {
  if (!ready) {
    return;
  }

  const bounds = getBounds();
  if (!bounds) {
    return;
  }

  mapRef.value.map.fitBounds(bounds, 100);

  google.maps.event.addListenerOnce(mapRef.value.map, 'idle', () => {
    const currentZoom = mapRef.value.map.getZoom();

    if (currentZoom > 14) {
      mapRef.value.map.setZoom(14);
    }
  });
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});
</script>

<template>
  <div class="page-brand-map" :class="{ 'show-map': isMapDisplayed }">
    <GoogleMap
      ref="mapRef"
      :api-key="apiKey"
      gesture-handling="greedy"
      :clickable-icons="false"
      :street-view-control="false"
      :map-type-control="false"
      :fullscreen-control="false"
      :zoom-control="!isMobileOrTablet"
      zoom-control-position="TOP_RIGHT"
      :styles=" [
        { featureType: 'poi', stylers: [ { visibility: 'off' } ] },
        { featureType: 'transit.station.bus', stylers: [ { visibility: 'off' } ] },
      ]"
    >
      <PageSpaceMarker
        v-for="pageSpace in secondaryPageSpaceMarkers"
        :key="pageSpace.id"
        :page-space="pageSpace"
        is-secondary
      />

      <PageSpaceMarker
        v-for="pageSpace in primaryPageSpaceMarkers"
        :key="pageSpace.id"
        :page-space="pageSpace"
      />
    </GoogleMap>

    <StickyTabs v-show="isMobileOrTablet" :current-view="mobileView" @set-view="setMobileView" />
  </div>
</template>
